import { useAuth0 } from '@auth0/auth0-react';
import { Button, Spinner } from '@blueprintjs/core';
import { ErrorBox } from '@pi/ui';
import React, { useEffect, useRef, useState } from 'react';
import { SessionDocument } from 'src/generated/graphql';
import apolloClient from 'src/startup/apollo';
import { clearJwt, setupAuth0Session } from 'src/utils/jwt';

import { takePendingReturnTo } from './returnTo';

import type { ServerError } from '@apollo/client';

/**
 * A non-2xx HTTP response never reaches the GraphQL layer, so Apollo's `ApolloError.message` is just
 * the generic "Response not successful: Received status code ###" - authorizationMiddleware's real
 * reason (or its deliberately generic one) lives in the parsed body at networkError.result.message.
 */
const getAuth0ErrorMessage = (error: Error): string => {
    const networkError = (error as { networkError?: unknown }).networkError as
        | ServerError
        | undefined;
    const resultMessage = (networkError?.result as { message?: unknown } | undefined)?.message;

    return typeof resultMessage === 'string' ? resultMessage : error.message;
};

/**
 * Handles the redirect back from Auth0.
 *
 * The SDK has already exchanged the authorization code by the time this renders, so what is left is
 * to mark the session as Auth0-backed, confirm api-v2 accepts the token, and send the user on to
 * whatever they originally asked for.
 */
export const Auth0Callback: React.FC = () => {
    const {
        isLoading,
        isAuthenticated,
        error: auth0Error,
        getAccessTokenSilently,
        loginWithRedirect,
        logout,
    } = useAuth0();
    const [error, setError] = useState<Error | null>(null);
    const started = useRef(false);

    useEffect(() => {
        if (isLoading || !isAuthenticated || started.current) return;
        started.current = true;

        const run = async () => {
            const returnTo = takePendingReturnTo();

            try {
                // Must come before any request: the credential travels in the `jwt` header, and the
                // token provider only reaches for the SDK once the session is marked Auth0.
                setupAuth0Session();
                await getAccessTokenSilently();

                // Establishes the PI-side record (api-v2 links or provisions on this first request)
                // and, just as importantly, surfaces a refusal *here* - where we can show a way out
                // - rather than after we have navigated away.
                await apolloClient.query({
                    query: SessionDocument,
                    fetchPolicy: 'network-only',
                    context: { noErrorAlert: true },
                });

                // A real navigation, not history.replaceState: both AppRouter branches return a
                // <BrowserRouter> in the same position, so React updates rather than remounts it and
                // its internal location would stay on the callback route - rendering the 404 while
                // the address bar looked correct. `replace` also drops the spent ?code= from history.
                window.location.replace(returnTo);
            } catch (ex) {
                // Leave nothing half-established behind. Clearing the PI marker alone is not enough:
                // the SDK caches its own session, so the next attempt would silently reuse the very
                // identity that was just rejected and never show the Auth0 login screen again.
                clearJwt();
                setError(new Error(getAuth0ErrorMessage(ex as Error)));

                try {
                    // openUrl: false drops the SDK's cached session without a redirect, so the
                    // error stays on screen.
                    await logout({ openUrl: false });
                } catch {
                    // Best effort - the failure we need the user to see is the one above.
                }
            }
        };

        void run();
    }, [isLoading, isAuthenticated, getAccessTokenSilently, logout]);

    const displayError = error || auth0Error;

    if (displayError) {
        return (
            <div style={{ padding: 24 }}>
                <ErrorBox error={displayError} />
                <p style={{ marginTop: 16 }}>
                    This Auth0 account cannot sign in to PI. Try another account, or sign in with
                    your PI email and password.
                </p>
                <p style={{ marginTop: 8 }}>
                    <Button
                        intent='primary'
                        data-testid='auth0-retry'
                        onClick={() =>
                            void loginWithRedirect({
                                authorizationParams: { prompt: 'login' },
                                appState: { returnTo: '/' },
                            })
                        }
                    >
                        Try a different account
                    </Button>{' '}
                    <a href='/' style={{ marginLeft: 8 }}>
                        Back to login
                    </a>
                </p>
            </div>
        );
    }

    return <Spinner />;
};
